import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { generatePath, useNavigate } from 'react-router-dom';
import {
    Calendar,
    Clock,
    Edit,
    ClipboardText,
    DocumentText,
    Sms,
    MoreVertical,
    Call,
    Trash,
} from '@/components/icons';
import PatientAssignModal from '@/components/patients/PatientAssignModal';
import { ReportData } from '@/components/preview/types';
import { CardActionButton } from '@/components/system';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useModalContext } from '@/components/ui/modal-provider';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PatientDetailPage, PatientsPage, ReportPage } from '@/pages/constants';
import { Patient } from '@/types/patient';
import { createPageUrl, getReportLink, translateGender } from '@/utils';
import { PatientAvatar } from './PatientAvatar';
import type { KeyboardEvent, MouseEvent } from 'react';

interface PatientCardProps {
    patient: Patient;
    onEdit: () => void;
    onDelete: () => void;
    ongoingReport?: ReportData;
}

export default function PatientCard({
    patient,
    onEdit,
    onDelete,
    ongoingReport,
}: PatientCardProps) {
    const navigate = useNavigate();
    const { openWizardModal } = useModalContext();

    const parseBirthDate = (dob: string | undefined): Date | null => {
        if (!dob) return null;

        const [datePart] = dob.split('T');
        const [year, month, day] = datePart.split('-').map(Number);

        if (!year || !month || !day) return null;

        return new Date(year, month - 1, day);
    };

    const calculateAge = (dob: string | undefined): number | null => {
        const birthDate = parseBirthDate(dob);
        if (!birthDate) return null;

        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const age = patient.age || calculateAge(patient.date_of_birth);
    const birthDate = parseBirthDate(patient.date_of_birth);

    // `age` is 0 for a patient under one year old, so it has to be compared
    // against null — a falsy check drops the badge and leaks a bare "0".
    const ageLabel =
        age == null ? null : age === 0 ? 'Menos de 1 ano' : age === 1 ? '1 ano' : `${age} anos`;

    const handleStartReport = () => {
        if (ongoingReport) {
            navigate(getReportLink(ongoingReport.id));
            return;
        }

        navigate(createPageUrl(ReportPage), {
            state: {
                preSelectedPatient: {
                    id: patient.id,
                    full_name: patient.full_name,
                    date_of_birth: patient.date_of_birth,
                    age: age,
                    gender: patient.gender,
                    avatar_url: patient.avatar_url,
                },
            },
        });
    };

    const handleAssignSurvey = () => {
        openWizardModal({
            content: <PatientAssignModal patient={patient} mode="surveys" />,
            size: 'md',
            className: 'max-h-[95vh]',
        });
    };

    const handleAssignTest = () => {
        openWizardModal({
            content: <PatientAssignModal patient={patient} mode="tests" />,
            size: 'lg',
            className: 'max-h-[95vh]',
        });
    };

    const handleOpenPatient = () => {
        navigate(`/${generatePath(PatientDetailPage, { patientId: patient.id })}`, {
            state: { backTo: createPageUrl(PatientsPage) },
        });
    };

    const stopPropagation = (event: MouseEvent) => {
        event.stopPropagation();
    };

    const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        handleOpenPatient();
    };

    return (
        <Card
            onClick={handleOpenPatient}
            onKeyDown={handleCardKeyDown}
            role="button"
            tabIndex={0}
            className="flex h-full cursor-pointer flex-col overflow-hidden transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5 hover:border-brand/20 hover:shadow-elevation-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
            <CardContent className="flex flex-1 flex-col p-4 sm:p-6">
                <div className="mb-4 flex items-start justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                        <PatientAvatar
                            name={patient.full_name}
                            avatarUrl={patient.avatar_url}
                            className="size-11 sm:size-12"
                        />
                        <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 items-center gap-2">
                                <h3 className="truncate text-base font-bold text-foreground sm:text-lg">
                                    {patient.full_name}
                                </h3>
                                {patient.intake_pending && (
                                    <TooltipProvider delayDuration={0}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="cursor-help text-amber-600 hover:text-amber-700">
                                                    <Clock className="size-4" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">
                                                Aguardando resposta do formulário de anamnese
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2 sm:mt-1">
                                {ageLabel && (
                                    <Badge variant="secondary" className="text-xs">
                                        {ageLabel}
                                    </Badge>
                                )}
                                {!ageLabel && patient.intake_pending && (
                                    <Skeleton className="h-5 w-14" />
                                )}
                                {patient.gender && (
                                    <Badge variant="outline" className="text-xs capitalize">
                                        {translateGender(patient.gender)}
                                    </Badge>
                                )}
                                {!patient.gender && patient.intake_pending && (
                                    <Skeleton className="h-5 w-16" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 space-y-2 text-sm">
                    {patient.email && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Sms className="size-4 shrink-0 text-muted-foreground" />
                            <span className="truncate">{patient.email}</span>
                        </div>
                    )}
                    {!patient.email && patient.intake_pending && (
                        <div className="flex items-center gap-2">
                            <Sms className="size-4 text-muted-foreground" />
                            <Skeleton className="h-4 w-40" />
                        </div>
                    )}
                    {patient.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Call className="size-4 shrink-0 text-muted-foreground" />
                            <span className="truncate">{patient.phone}</span>
                        </div>
                    )}
                    {!patient.phone && patient.intake_pending && (
                        <div className="flex items-center gap-2">
                            <Call className="size-4 text-muted-foreground" />
                            <Skeleton className="h-4 w-32" />
                        </div>
                    )}
                    {patient.date_of_birth && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="size-4 shrink-0 text-muted-foreground" />
                            <span className="truncate tabular-nums">
                                {birthDate
                                    ? format(birthDate, 'dd/MM/yyyy', {
                                          locale: ptBR,
                                      })
                                    : '-'}
                            </span>
                        </div>
                    )}
                    {!patient.date_of_birth && patient.intake_pending && (
                        <div className="flex items-center gap-2">
                            <Calendar className="size-4 text-muted-foreground" />
                            <Skeleton className="h-4 w-28" />
                        </div>
                    )}
                </div>

                <div className="mt-4 flex items-center gap-2 border-t border-border/70 pt-4">
                    <CardActionButton
                        onClick={(event: MouseEvent) => {
                            stopPropagation(event);
                            handleStartReport();
                        }}
                        className="min-w-0 flex-1"
                    >
                        {ongoingReport ? 'Continuar laudo' : 'Iniciar laudo'}
                    </CardActionButton>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={stopPropagation}
                                className="size-10 shrink-0"
                            >
                                <MoreVertical className="size-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem
                                onClick={(event: MouseEvent) => {
                                    stopPropagation(event);
                                    handleAssignSurvey();
                                }}
                            >
                                <DocumentText className="mr-2 size-4" />
                                Enviar formulário
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={(event: MouseEvent) => {
                                    stopPropagation(event);
                                    handleAssignTest();
                                }}
                            >
                                <ClipboardText className="mr-2 size-4" />
                                Enviar instrumento
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={(event: MouseEvent) => {
                                    stopPropagation(event);
                                    onEdit();
                                }}
                            >
                                <Edit className="mr-2 size-4" />
                                Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={(event: MouseEvent) => {
                                    stopPropagation(event);
                                    onDelete();
                                }}
                                className="text-red-600 focus:text-red-600"
                            >
                                <Trash className="mr-2 size-4" />
                                Deletar
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </CardContent>
        </Card>
    );
}
